import { useEffect, useState } from 'react';
import { PageHeader, TableSkeleton } from '@/components/shared/page';
import { Can } from '@/components/shared/can';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, Switch, Textarea } from '@/components/ui/misc';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FormError } from '@/features/auth/auth-layout';
import { ApiRequestError } from '@/lib/api';
import { CURRENCIES, TIMEZONES, timezoneLabel } from '@/lib/currencies';
import { titleCase } from '@/lib/utils';
import type { Organization, Sequence } from '@/lib/types';
import {
  useOrganization,
  useSaveSequence,
  useSequences,
  useUpdateOrganization,
} from './api';

export function OrganizationPage() {
  const { data: organization, isLoading } = useOrganization();

  if (isLoading || !organization) {
    return (
      <div>
        <PageHeader title="Organisation" />
        <TableSkeleton />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organisation"
        description="Company details, security policy and document numbering."
      />
      <ProfileCard organization={organization} />
      <SecurityCard organization={organization} />
      <NumberingCard />
    </div>
  );
}

function ProfileCard({ organization }: { organization: Organization }) {
  const update = useUpdateOrganization();
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: organization.name,
    legalName: organization.legalName ?? '',
    baseCurrency: organization.baseCurrency,
    timezone: organization.timezone,
    taxNumber: organization.taxNumber ?? '',
    phone: organization.phone ?? '',
    email: organization.email ?? '',
    website: organization.website ?? '',
    address: organization.address ?? '',
  });

  const save = async () => {
    setError(null);
    try {
      await update.mutateAsync(form);
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Company details</CardTitle>
        <CardDescription>
          These appear on quotes, invoices and anything else sent to a client.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Trading name">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Legal name">
            <Input
              value={form.legalName}
              onChange={(e) => setForm({ ...form, legalName: e.target.value })}
            />
          </Field>
          <Field label="Base currency" hint="All reporting is converted to this currency.">
            <Select
              value={form.baseCurrency}
              onChange={(e) => setForm({ ...form, baseCurrency: e.target.value })}
            >
              {/* A currency already saved but not on the list is still a
                  currency; keep it selectable rather than silently changing it. */}
              {!CURRENCIES.some((currency) => currency.code === form.baseCurrency) && (
                <option value={form.baseCurrency}>{form.baseCurrency}</option>
              )}
              {CURRENCIES.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.code} — {currency.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Timezone" hint="Working hours, due dates and reminders follow this.">
            <Select value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })}>
              {!TIMEZONES.includes(form.timezone) && (
                <option value={form.timezone}>{form.timezone}</option>
              )}
              {TIMEZONES.map((zone) => (
                <option key={zone} value={zone}>
                  {timezoneLabel(zone)}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Tax registration number (TRN)">
            <Input
              value={form.taxNumber}
              onChange={(e) => setForm({ ...form, taxNumber: e.target.value })}
            />
          </Field>
          <Field label="Phone">
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label="Email">
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Field>
          <Field label="Website">
            <Input
              value={form.website}
              placeholder="https://"
              onChange={(e) => setForm({ ...form, website: e.target.value })}
            />
          </Field>
        </div>

        <Field label="Registered address">
          <Textarea
            value={form.address}
            rows={3}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
        </Field>

        <FormError message={error} />

        <Can permission="admin.organization.update">
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save details'}
          </Button>
        </Can>
      </CardContent>
    </Card>
  );
}

function SecurityCard({ organization }: { organization: Organization }) {
  const update = useUpdateOrganization();
  const [error, setError] = useState<string | null>(null);
  const [enforce2fa, setEnforce2fa] = useState(organization.enforce2fa);
  const [idleMinutes, setIdleMinutes] = useState(organization.sessionIdleMinutes);
  const [maxSessions, setMaxSessions] = useState(organization.maxConcurrentSessions);
  const [allowlist, setAllowlist] = useState(organization.adminIpAllowlist.join('\n'));

  const save = async () => {
    setError(null);
    try {
      await update.mutateAsync({
        enforce2fa,
        sessionIdleMinutes: Number(idleMinutes),
        maxConcurrentSessions: Number(maxSessions),
        adminIpAllowlist: allowlist
          .split('\n')
          .map((line) => line.trim())
          .filter(Boolean),
      });
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'Could not save.');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Security</CardTitle>
        <CardDescription>Applies to everyone in the workspace.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <label className="flex items-start justify-between gap-4 rounded-md border p-3">
          <span>
            <span className="block text-sm font-medium">
              Require two-factor authentication
            </span>
            <span className="block text-xs text-muted-foreground">
              Everyone must set up an authenticator app before they can sign in. Turning this on
              locks out anyone who has not yet enrolled, so tell the team first.
            </span>
          </span>
          <Switch checked={enforce2fa} onCheckedChange={setEnforce2fa} />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Idle timeout (minutes)">
            <Input
              type="number"
              min={5}
              value={idleMinutes}
              onChange={(e) => setIdleMinutes(Number(e.target.value))}
            />
          </Field>
          <Field label="Maximum concurrent sessions">
            <Input
              type="number"
              min={1}
              value={maxSessions}
              onChange={(e) => setMaxSessions(Number(e.target.value))}
            />
          </Field>
        </div>

        <Field
          label="Admin IP allowlist"
          hint="One CIDR or address per line. Leave empty to allow any address."
        >
          <Textarea
            value={allowlist}
            rows={3}
            placeholder="203.0.113.0/24"
            onChange={(e) => setAllowlist(e.target.value)}
          />
        </Field>

        <FormError message={error} />

        <Can permission="admin.organization.update">
          <Button onClick={save} disabled={update.isPending}>
            {update.isPending ? 'Saving…' : 'Save security settings'}
          </Button>
        </Can>
      </CardContent>
    </Card>
  );
}

function NumberingCard() {
  const { data: sequences = [], isLoading } = useSequences();
  const save = useSaveSequence();
  const [drafts, setDrafts] = useState<Record<string, Sequence>>({});

  useEffect(() => {
    setDrafts(Object.fromEntries(sequences.map((sequence) => [sequence.docType, sequence])));
  }, [sequences]);

  const update = (docType: string, patch: Partial<Sequence>) =>
    setDrafts((current) => ({ ...current, [docType]: { ...current[docType], ...patch } }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Document numbering</CardTitle>
        <CardDescription>
          Invoice numbers are allocated under a row lock, so they are gap-free even when two people
          bill at the same moment. The counter can only move forward.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <TableSkeleton columns={5} />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Document</TableHead>
                <TableHead>Prefix</TableHead>
                <TableHead>Padding</TableHead>
                <TableHead>Resets</TableHead>
                <TableHead>Next</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sequences.map((sequence) => {
                const draft = drafts[sequence.docType] ?? sequence;
                return (
                  <TableRow key={sequence.id}>
                    <TableCell className="font-medium">{titleCase(sequence.docType)}</TableCell>
                    <TableCell>
                      <Input
                        value={draft.prefix}
                        className="h-8 w-20"
                        onChange={(e) => update(sequence.docType, { prefix: e.target.value })}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="number"
                        min={1}
                        max={10}
                        value={draft.padding}
                        className="h-8 w-16"
                        onChange={(e) =>
                          update(sequence.docType, { padding: Number(e.target.value) })
                        }
                      />
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {draft.resetPeriod}
                    </TableCell>
                    <TableCell className="tabular text-sm">{draft.nextNumber}</TableCell>
                    <TableCell>
                      <Can permission="admin.organization.update">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={save.isPending}
                          onClick={() =>
                            save.mutate({
                              docType: draft.docType,
                              prefix: draft.prefix,
                              padding: draft.padding,
                              resetPeriod: draft.resetPeriod,
                              nextNumber: draft.nextNumber,
                            })
                          }
                        >
                          Save
                        </Button>
                      </Can>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}
